import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

export default function AuthRegister({ mode = "senior" }) {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const isJuniorMode = mode === "junior";
  const listEndpoint = isJuniorMode
    ? "/api/v2/auth/public/registration-medecins?category=junior"
    : "/api/v2/auth/public/registration-medecins?category=senior";
  const registerEndpoint = isJuniorMode
    ? "/api/v2/auth/register-junior"
    : "/api/v2/auth/register";
  const alternatePath = isJuniorMode ? "/auth/register" : "/auth/register-interne";

  const [medecins, setMedecins] = useState([]);
  const [loadingMed, setLoadingMed] = useState(false);

  const [selected, setSelected] = useState(""); // "NOM||PRENOM"
  const [email, setEmail] = useState(params.get("email") || "");

  const [loading, setLoading] = useState(false);
  const [ok, setOk] = useState(null);
  const [error, setError] = useState(null);

  const [resendLoading, setResendLoading] = useState(false);
  const [resendMsg, setResendMsg] = useState(null);
  const [resendEmail, setResendEmail] = useState(params.get("email") || "");
  const [resendRetryAt, setResendRetryAt] = useState(0);
  const [resendRetryUnknown, setResendRetryUnknown] = useState(false);
  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    if (!resendRetryAt) return;
    const timer = setInterval(() => {
      const current = Date.now();
      setNow(current);
      if (current >= resendRetryAt) setResendRetryAt(0);
    }, 1000);
    return () => clearInterval(timer);
  }, [resendRetryAt]);

  useEffect(() => {
    async function load() {
      setLoadingMed(true);
      try {
        const res = await fetch(listEndpoint, { credentials: "omit" });
        const json = await res.json().catch(() => ({}));
        if (res.ok && json?.ok && Array.isArray(json.medecins)) {
          setMedecins(json.medecins);
        } else {
          setError(json?.error || "Impossible de charger la liste des médecins.");
        }
      } catch {
        setError("Impossible de charger la liste des médecins. Réessayez dans quelques instants.");
      } finally {
        setLoadingMed(false);
      }
    }
    load();
  }, [listEndpoint]);

  function parseSelected(v) {
    const [nom, prenom] = String(v || "").split("||");
    return { nom: nom || "", prenom: prenom || "" };
  }

  async function onSubmit(e) {
    e.preventDefault();
    setLoading(true);
    setOk(null);
    setError(null);
    setResendMsg(null);

    const { nom, prenom } = parseSelected(selected);

    try {
      const res = await fetch(registerEndpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          nom: String(nom).trim(),
          prenom: String(prenom).trim(),
          email: String(email).trim(),
        }),
      });

      const json = await res.json().catch(() => ({}));
      if (!res.ok || !json?.ok) {
        setError(json?.error || "Erreur lors de la création du compte.");
        setLoading(false);
        return;
      }

      setOk(json);
      setLoading(false);
    } catch {
      setError("Erreur réseau.");
      setLoading(false);
    }
  }

  async function onResend(event) {
    event.preventDefault();
    if (resendLoading || resendRetryUnknown || resendRetryAt > Date.now() || !resendEmail.trim()) return;
    setResendMsg(null);
    setResendLoading(true);
    try {
      const res = await fetch("/api/v2/auth/request-activation-link", {
        method: "POST", credentials: "omit",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: resendEmail.trim() })
      });
      const json = await res.json().catch(() => ({}));
      if (res.status === 429) {
        const raw = res.headers.get("Retry-After");
        const deadline = raw && /^\d+$/.test(raw) ? Date.now() + Number(raw) * 1000 : Date.parse(raw);
        setNow(Date.now());
        setResendRetryAt(Number.isFinite(deadline) ? deadline : 0);
        setResendRetryUnknown(!Number.isFinite(deadline));
        setResendMsg("Trop de demandes. Patientez, puis rechargez la page pour réessayer.");
      } else if (res.ok && json?.ok) {
        setResendMsg(json.message);
      } else {
        setResendMsg("Le service est temporairement indisponible. Réessayez plus tard.");
      }
    } catch {
      setResendMsg("Erreur réseau. Réessayez plus tard.");
    } finally {
      setResendLoading(false);
    }
  }

  const showForgot =
    !!error &&
    /déjà activé|mot de passe oublié|password|reset/i.test(String(error));

  const S = {
    container: {
      minHeight: "100dvh",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      background: "#f3f4f6",
      padding: "clamp(24px, 4dvh, 48px) 28px",
      fontFamily:
        "Inter, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
    },
    card: {
      width: "clamp(760px, 54vw, 1180px)",
      maxWidth: "calc(100vw - 56px)",
      background: "#fff",
      border: "1px solid #e5e7eb",
      borderRadius: "24px",
      padding: "clamp(38px, 4vw, 56px)",
      boxShadow: "0 22px 56px rgba(15,23,42,0.12)",
    },
    h1: { margin: 0, fontSize: "40px", fontWeight: 950, color: "#111827" },
    p: {
      marginTop: "10px",
      color: "#4b5563",
      lineHeight: 1.55,
      fontSize: "17px",
    },
    form: { marginTop: "24px", display: "grid", gap: "16px" },
    label: { fontSize: "15px", fontWeight: 800, color: "#111827" },
    input: {
      width: "100%",
      padding: "15px 14px",
      borderRadius: "12px",
      border: "1px solid #d1d5db",
      outline: "none",
      fontSize: "16px",
    },
    row: { display: "flex", gap: "12px", flexWrap: "wrap", marginTop: "14px" },
    btn: {
      padding: "15px 22px",
      borderRadius: "9999px",
      border: "1px solid #111827",
      background: "#111827",
      color: "#fff",
      fontSize: "15px",
      fontWeight: 900,
      cursor: "pointer",
    },
    btnLight: {
      padding: "15px 22px",
      borderRadius: "9999px",
      border: "1px solid #d1d5db",
      background: "#fff",
      color: "#111827",
      fontSize: "15px",
      fontWeight: 900,
      cursor: "pointer",
    },
    alertOk: {
      marginTop: "14px",
      padding: "12px",
      borderRadius: "12px",
      border: "1px solid #bbf7d0",
      background: "#f0fdf4",
      color: "#166534",
      fontSize: "14px",
      lineHeight: 1.5,
      wordBreak: "break-word",
    },
    alertErr: {
      marginTop: "14px",
      padding: "12px",
      borderRadius: "12px",
      border: "1px solid #fecaca",
      background: "#fef2f2",
      color: "#991b1b",
      fontSize: "14px",
      lineHeight: 1.5,
      wordBreak: "break-word",
    },
    small: { fontSize: "13px", color: "#6b7280" },
    pre: { marginTop: "8px", fontSize: "13px", color: "#111827" },
    hint: { marginTop: "10px", fontSize: "13px", color: "#374151" },
    emailWarning: {
      marginTop: "14px",
      padding: "12px",
      borderRadius: "10px",
      border: "1px solid #f59e0b",
      background: "#fffbeb",
      color: "#78350f",
      fontSize: "15px",
      lineHeight: 1.45,
    },
    switchBox: {
      marginTop: "16px",
      padding: "12px",
      borderRadius: "10px",
      border: "1px solid #dbeafe",
      background: "#eff6ff",
      color: "#1e3a8a",
      fontSize: "15px",
      lineHeight: 1.45,
    },
    linkButton: {
      border: "none",
      background: "transparent",
      color: "#1d4ed8",
      font: "inherit",
      fontWeight: 900,
      textDecoration: "underline",
      cursor: "pointer",
      padding: 0,
    },
  };

  return (
    <div style={S.container}>
      <div style={S.card}>
        <h1 style={S.h1}>
          {isJuniorMode
            ? "Créer mon compte interne / junior"
            : "Créer mon compte"}
        </h1>
        <p style={S.p}>
          Choisissez votre <b>Nom / Prénom</b> dans la liste du service puis
          saisissez votre <b>Email</b>. Votre demande sera transmise au service
          pour validation. Après validation, vous recevrez un lien et un code
          secret pour <b>définir votre mot de passe</b>.
        </p>
        <div style={S.emailWarning}>
          <b>Adresse email recommandée :</b> utilisez de préférence une adresse
          personnelle. Les emails automatiques peuvent ne pas arriver sur les
          adresses AP-HP.
        </div>
        <div style={S.switchBox}>
          {isJuniorMode ? (
            <>
              Vous êtes médecin senior du service ?{" "}
              <button
                type="button"
                style={S.linkButton}
                onClick={() => navigate(alternatePath)}
              >
                Utilisez l'inscription médecin senior.
              </button>
            </>
          ) : (
            <>
              Vous êtes interne, docteur junior ou stagiaire associé ?{" "}
              <button
                type="button"
                style={S.linkButton}
                onClick={() => navigate(alternatePath)}
              >
                Utilisez l'inscription junior.
              </button>
            </>
          )}
        </div>

        <form style={S.form} onSubmit={onSubmit}>
          <div>
            <div style={S.label}>Médecin (Nom / Prénom)</div>
            <select
              style={S.input}
              value={selected}
              onChange={(e) => setSelected(e.target.value)}
              disabled={loadingMed}
            >
              <option value="">
                {loadingMed ? "Chargement de la liste..." : "— Sélectionner —"}
              </option>
              {medecins.map((m) => {
                const key = `${m.nom}||${m.prenom}`;
                return (
                  <option key={key} value={key}>
                    {m.nom} — {m.prenom}
                  </option>
                );
              })}
            </select>
            <div style={S.small}>
              Si vous n’apparaissez pas : contactez christophe.baillard@aphp.fr
            </div>
          </div>

          <div>
            <div style={S.label}>Email</div>
            <input
              style={S.input}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              type="email"
              autoComplete="email"
            />
            <div style={S.small}>
              Utilisez une adresse personnelle que vous pouvez consulter.
            </div>
          </div>

          <div style={S.row}>
            <button
              type="submit"
              style={S.btn}
              disabled={loading || !selected || !email.trim()}
            >
              {loading ? "Envoi..." : "Demander la création du compte"}
            </button>
            <button
              type="button"
              style={S.btnLight}
              onClick={() => { window.location.href = "/auth"; }}
            >
              Retour
            </button>
          </div>
        </form>

        <section aria-labelledby="activation-resend-title" style={{ marginTop: 28, borderTop: "1px solid #e5e7eb", paddingTop: 20 }}>
          <h2 id="activation-resend-title" style={{ fontSize: 22, margin: 0 }}>Vous n'avez pas reçu le lien d'activation ?</h2>
          <form style={S.form} onSubmit={onResend}>
            <label htmlFor="activation-resend-email" style={S.label}>Adresse email utilisée à l'inscription</label>
            <input id="activation-resend-email" style={S.input} type="email" autoComplete="email" required
              value={resendEmail} onChange={event => setResendEmail(event.target.value)} />
            <div style={S.row}>
              <button type="submit" style={S.btnLight}
                disabled={resendLoading || resendRetryUnknown || resendRetryAt > now || !resendEmail.trim()}>
                {resendLoading ? "Envoi..." : "Recevoir un nouveau lien"}
              </button>
            </div>
            {resendMsg && <div role="status" style={S.hint}>{resendMsg}</div>}
            {resendRetryAt > now && <div role="status" style={S.hint}>Réessayez dans {Math.ceil((resendRetryAt - now) / 1000)} s.</div>}
          </form>
        </section>

        {error && (
          <div style={S.alertErr}>
            <b>{error}</b>

            {showForgot ? (
              <div style={{ marginTop: "10px" }}>
                <button
                  type="button"
                  style={S.btnLight}
                  onClick={() =>
                    navigate(`/auth/forgot?email=${encodeURIComponent(email.trim())}`)
                  }
                >
                  Mot de passe oublié
                </button>
              </div>
            ) : null}
          </div>
        )}

        {ok && (
          <div style={S.alertOk}>
            <b>Demande transmise ✅</b>
            <div style={S.pre}>
              Identifiant : <b>{ok?.user_public?.username}</b>
            </div>
            <div style={{ marginTop: "12px", fontSize: "14px", lineHeight: 1.6 }}>
              <b>Validation par le service en attente</b>
              <div style={{ marginTop: "8px" }}>
                Votre demande d’inscription a été envoyée au service. Après
                validation, vous recevrez un email contenant :
                <ul style={{ marginTop: "8px", paddingLeft: "20px" }}>
                  <li>Un <b>lien d'activation</b> pour finaliser votre inscription</li>
                  <li>Un <b>code secret</b> à saisir lors de la finalisation</li>
                </ul>
              </div>
              <div style={{ marginTop: "12px", padding: "10px", background: "#fff", borderRadius: "8px", border: "1px solid #bbf7d0" }}>
                <b>Important :</b> tant que la demande n’est pas validée, le
                compte ne permet pas encore de se connecter. Vérifiez aussi vos
                spams/courrier indésirable après validation.
              </div>
            </div>

            <div style={{ marginTop: 16, display: "flex", gap: 10, flexWrap: "wrap" }}>
              <button
                type="button"
                style={S.btn}
                onClick={() => navigate("/auth/activate")}
                title="Une fois que vous avez reçu l'email avec le lien et le code secret"
              >
                J’ai reçu l’email d’activation → Finaliser
              </button>

              <button
                type="button"
                style={S.btnLight}
                onClick={() => { window.location.href = "/auth"; }}
              >
                Retour accueil
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
